import axios from 'axios';
import { storage } from 'utils';
import type { AuthFactorType, IAuthChallengeState, IAuthSmsOptedOut } from '@repo/types';

const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:3040';

/**
 * VRG-490 §2 — the discriminants `verifyMagicAuthCode` / `loginWithPassword`
 * return in place of tokens once a verified second factor exists. Owners
 * have no mandatory-enrollment tier (D1 is `platform_admin`-only), so
 * `enrollmentRequired` should never actually appear on this app's login
 * responses — included for shape parity with the platform service and so a
 * caller can defensively check it without a type error.
 */
export interface TwoFactorChallengeFields {
  challengeRequired?: boolean;
  ticket?: string;
  factorType?: AuthFactorType;
  /** The challenged SMS number replied STOP, so no code was texted (the ticket still works). */
  smsOptedOut?: IAuthSmsOptedOut;
  enrollmentRequired?: boolean;
}

// Add response interceptor to handle COMPANY_SUSPENDED for already-authenticated users
axios.interceptors.response.use(
  response => response,
  error => {
    const errorData = error?.response?.data;
    const isCompanySuspended =
      error?.response?.status === 403 &&
      (errorData?.code === 'COMPANY_SUSPENDED' ||
        (typeof errorData?.message === 'object' && errorData?.message?.code === 'COMPANY_SUSPENDED') ||
        (typeof errorData?.message === 'string' && errorData?.message?.includes('suspended')));

    if (isCompanySuspended && typeof window !== 'undefined') {
      // Redirect to the suspended page for already-authenticated users
      if (!window.location.pathname.includes('/company-suspended')) {
        window.location.href = '/company-suspended';
      }
    }

    return Promise.reject(error);
  }
);

interface AuthState {
  app: 'customer' | 'admin';
  origin?: string;
  embeddKey?: string;
  companyId?: string;
}

function encodeState(data: AuthState): string {
  return btoa(JSON.stringify(data));
}

export class WorkOSAuthService {
  /**
   * Shared body for the social-login redirect flows (Google, Apple, ...).
   * Stashes embed context in sessionStorage, builds the WorkOS `state`, and
   * redirects to the API's `/auth/login` endpoint for the given provider.
   */
  private loginWithSocialProvider(
    provider: string,
    context?: { embeddKey?: string; companyId?: string }
  ) {
    // Store context before redirect for restoration after callback
    if (context?.embeddKey) {
      sessionStorage.setItem('embeddKey', context.embeddKey);
    }
    if (context?.companyId) {
      sessionStorage.setItem('companyId', context.companyId);
    }

    // VRG-709 — `origin` is where the API sends the person back after the callback, so it
    // must be THIS host (app.vera.inc or <brand>.app.vera.inc): the tokens are written to
    // this origin's storage. The ORIGIN only, not `href` — the API keeps nothing but the
    // origin anyway, and a full URL would carry the path and query through WorkOS for no
    // reason. The API allowlists it (helpers/customer-app-origin.ts); an origin it does not
    // recognise falls back to app.vera.inc rather than being trusted.
    const state = encodeState({
      app: 'customer',
      origin: window.location.origin,
      embeddKey: context?.embeddKey,
      companyId: context?.companyId,
    });

    // Redirect to API auth endpoint with customer userType. `state` is base64, whose `+`
    // a query parser reads as a space, so it is encoded like any other query value.
    const loginUrl = `${API_URL}/auth/login?provider=${provider}&userType=customer&state=${encodeURIComponent(state)}`;
    window.location.href = loginUrl;
  }

  /**
   * Initiate Google OAuth flow
   * Opens OAuth in new window/tab
   */
  loginWithGoogle(context?: { embeddKey?: string; companyId?: string }) {
    this.loginWithSocialProvider('GoogleOAuth', context);
  }

  /**
   * Initiate Apple OAuth flow (VERA-837)
   * Mirrors loginWithGoogle exactly, only the WorkOS provider differs.
   */
  loginWithApple(context?: { embeddKey?: string; companyId?: string }) {
    this.loginWithSocialProvider('AppleOAuth', context);
  }

  /**
   * Send Magic Auth code (WorkOS OTP)
   *
   * VERA-837: `companyId` (when known — the embed's company) is threaded
   * through so the API can send its OWN brand-themed OTP email (Studio
   * theme) alongside WorkOS's generic one, instead of relying on WorkOS
   * alone.
   */
  async sendMagicAuthCode(
    email: string,
    companyId?: string
  ): Promise<{ success: boolean; message: string; userType: string }> {
    const url = `${API_URL}/auth/magic-auth/send`;

    try {
      const response = await axios.get(url, {
        params: { email, userType: 'customer', companyId },
      });
      return response.data;
    } catch (error) {
      console.error('[WorkOS Auth] Failed to send magic auth code');
      throw error;
    }
  }

  /**
   * Verify Magic Auth code and authenticate
   */
  async verifyMagicAuthCode({
    email,
    code,
    companyId,
    embeddKey,
    firstName,
    lastName,
    username,
    acceptedTerms,
    termsVersion,
  }: {
    email: string;
    code: string;
    companyId?: string;
    embeddKey?: string;
    /**
     * Typed name from the self-serve create-account form (VERA-837). Sent
     * as query params so `CustomerWorkOSCallbackUseCase` can use them when
     * provisioning a NEW owner (WorkOS OTP profiles carry no name).
     */
    firstName?: string;
    lastName?: string;
    /** VRG-357 — chosen handle, provisioned onto the new owner. */
    username?: string;
    /**
     * VERA-837 — Terms & Conditions acceptance from the self-serve
     * create-account form's checkbox. The backend never assumes acceptance
     * — both fields are sent together only when the form actually collected
     * them.
     */
    acceptedTerms?: boolean;
    termsVersion?: string;
  }): Promise<{
    success: boolean;
    access_token?: string;
    refresh_token?: string;
    scan_token?: string;
    isNewUser?: boolean;
    /**
     * VRG-500 — did this email already have an account BEFORE this verification?
     * Distinct from `isNewUser`, which means "first time at this company" and is
     * false for a gift recipient whose `users` row and company link were both
     * provisioned by the send.
     */
    hasAccount?: boolean;
    // VRG-490: the challengeRequired/ticket/factorType discriminant. A 2FA-gated
    // login returns NO access_token, so callers must branch on this before storing.
  } & TwoFactorChallengeFields> {
    const url = `${API_URL}/auth/magic-auth/verify`;

    try {
      const params = {
        email,
        code,
        userType: 'customer',
        companyId: companyId,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        // VRG-357: the chosen handle rides the same query-param path as the name, so
        // a NEW owner is provisioned with one. OTP magic-auth carries no WorkOS
        // profile, so anything not sent here has to be back-filled later — and a
        // handle nobody set is the unaddressable-account problem this fixes.
        username: username || undefined,
        acceptedTerms: acceptedTerms || undefined,
        termsVersion: termsVersion || undefined,
      };

      const headers: Record<string, string> = {};

      if (embeddKey) {
        headers['X-Embedded-Key'] = embeddKey;
      }

      const response = await axios.get(url, { params, headers });

      if (response.data.success && response.data.access_token) {
        storage.setItem('accessToken', response.data.access_token);
        storage.setItem('refreshToken', response.data.refresh_token);
        storage.setItem('scanToken', response.data.scan_token);
      }

      return response.data;
    } catch (error) {
      console.error('[WorkOS Auth] Failed to verify magic auth code');
      throw error;
    }
  }

  /**
   * Authenticate with email + password (POST /auth/password/login).
   *
   * Uses the customer token triple (`access_token` / `refresh_token` /
   * `scan_token`), matching `verifyMagicAuthCode` above, so `checkAuthSaga`
   * (which requires all three in storage) survives a hard reload.
   */
  async loginWithPassword({
    email,
    password,
    companyId,
    embeddKey,
  }: {
    email: string;
    password: string;
    companyId?: string;
    embeddKey?: string;
  }): Promise<{
    success: boolean;
    // VRG-490 §2 — optional, not required: absent on a challengeRequired/
    // enrollmentRequired response (see TwoFactorChallengeFields below).
    access_token?: string;
    refresh_token?: string;
    scan_token?: string;
    isNewUser?: boolean;
  } & TwoFactorChallengeFields> {
    const url = `${API_URL}/auth/password/login`;

    try {
      const headers: Record<string, string> = {};
      if (embeddKey) {
        headers['X-Embedded-Key'] = embeddKey;
      }

      const response = await axios.post(
        url,
        { email, password, userType: 'customer', companyId },
        { headers }
      );

      // VRG-490 §2 — a verified second factor withholds the session:
      // `response.data.success` is still `true`, but there is no
      // `access_token` to store. Gate on the TOKEN, not just `success`
      // (the previous check would otherwise call `storage.setItem` with
      // `undefined` on a challenge/enrollment response).
      if (response.data.success && response.data.access_token) {
        storage.setItem('accessToken', response.data.access_token);
        storage.setItem('refreshToken', response.data.refresh_token);
        storage.setItem('scanToken', response.data.scan_token);
      }

      return response.data;
    } catch (error) {
      console.error('[WorkOS Auth] Failed to log in with password');
      throw error;
    }
  }

  /**
   * VRG-490 §2 — completes a login paused by a `challengeRequired` ticket
   * (from `verifyMagicAuthCode` / `loginWithPassword` above). `code` is
   * either the factor's own code (SMS/TOTP) OR a recovery code —
   * `POST /auth/2fa/challenge/verify` accepts both through the same field.
   * `@Public()` on the API side: there is no session yet, so no auth header.
   *
   * Throws on failure (matching this file's existing convention — see
   * `verifyMagicAuthCode`/`loginWithPassword` above) rather than resolving
   * with `success: false`. The API distinguishes a burned/unknown/expired
   * ticket (HTTP 404 — no point retrying, route back to sign-in) from an
   * incorrect code (HTTP 400 — the ticket is still alive); read
   * `error.response.status` at the call site the same way `SignInBanner`'s
   * `handlePasswordLogin` already reads it for the primary-factor path.
   */
  async verifyChallenge(
    ticket: string,
    code: string
  ): Promise<{
    success: boolean;
    access_token?: string;
    refresh_token?: string;
    scan_token?: string;
    isNewUser?: boolean;
    usedRecoveryCode?: boolean;
    remainingRecoveryCodes?: number;
  }> {
    const url = `${API_URL}/auth/2fa/challenge/verify`;

    try {
      const response = await axios.post(url, { ticket, code });

      if (response.data.success && response.data.access_token) {
        storage.setItem('accessToken', response.data.access_token);
        storage.setItem('refreshToken', response.data.refresh_token);
        storage.setItem('scanToken', response.data.scan_token);
      }

      return response.data;
    } catch (error) {
      console.error('[WorkOS Auth] Failed to verify 2FA challenge');
      throw error;
    }
  }

  /**
   * The challenge's current method and the alternatives it may switch to, plus `smsOptedOut`
   * when the SMS number replied STOP. Public, ticket-keyed (same endpoints as the platform).
   * Resolves null on any failure: the screen then just keeps its basic form.
   */
  async getChallengeOptions(ticket: string): Promise<IAuthChallengeState | null> {
    try {
      const response = await axios.post(`${API_URL}/auth/2fa/challenge/options`, { ticket });
      return response.data as IAuthChallengeState;
    } catch {
      return null;
    }
  }

  /**
   * Switch method / resend ("Try again"). On failure `errorCode` is the API's code —
   * `SMS_RECIPIENT_OPTED_OUT` when the number replied STOP, with `error` the "text START" line.
   */
  async switchChallengeFactor(
    ticket: string,
    factorId: string
  ): Promise<{ state?: IAuthChallengeState; error?: string; errorCode?: string }> {
    try {
      const response = await axios.post(`${API_URL}/auth/2fa/challenge/switch`, { ticket, factorId });
      return { state: response.data as IAuthChallengeState };
    } catch (error: any) {
      return {
        error: error?.response?.data?.message || 'Could not switch method. Please try again.',
        errorCode: error?.response?.data?.code,
      };
    }
  }

  async resendChallengeCode(ticket: string): Promise<{ state?: IAuthChallengeState; error?: string; errorCode?: string }> {
    try {
      const response = await axios.post(`${API_URL}/auth/2fa/challenge/resend`, { ticket });
      return { state: response.data as IAuthChallengeState };
    } catch (error: any) {
      return {
        error: error?.response?.data?.message || 'Could not send a code. Please try again.',
        errorCode: error?.response?.data?.code,
      };
    }
  }

  /**
   * Set or update the current user's password (POST /auth/password/setup).
   * Requires the caller to already be authenticated (Authorization: Bearer).
   */
  async setupPassword(password: string): Promise<{ success: boolean; message: string }> {
    const url = `${API_URL}/auth/password/setup`;
    const token = storage.getItem('accessToken');

    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await axios.post(
        url,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('[WorkOS Auth] Failed to set password');
      throw error;
    }
  }

  /**
   * Get customer profile
   */
  async getProfile() {
    const token = storage.getItem('accessToken');
    if (!token) {
      return null;
    }

    try {
      const url = `${API_URL}/auth/customer/profile`;
      const response = await axios.get(url, {
        headers: { 'X-Vera-Token': token },
      });
      return response.data;
    } catch (error) {
      console.error('[WorkOS Auth] Failed to get profile');
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!storage.getItem('accessToken');
  }

  /**
   * Get stored access token
   */
  getToken(): string | null {
    return storage.getItem('accessToken') ?? null;
  }

  /**
   * Logout user
   */
  async logout() {
    storage.removeItem('accessToken');
    storage.removeItem('refreshToken');
    storage.removeItem('scanToken');
  }
}

export const workosAuthService = new WorkOSAuthService();
